import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

import { faCheck, faChevronDown } from '@document-crunch/pro-regular-svg-icons'

import { cn } from '@dc-shared-web/lib'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-background placeholder:tw-text-muted-foreground focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 data-[state=open]:tw-rounded-b-none',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <Icon icon={faChevronDown} className='tw-h-4 tw-w-4 tw-opacity-50' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
  return (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'tw-min-w-[8rem] tw-overflow-hidden tw-rounded-md tw-border tw-bg-popover tw-text-popover-foreground tw-shadow-md data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2 data-[state=open]:tw-rounded-t-none data-[state=open]:tw-border-t-0 tw-w-[--radix-popper-anchor-width]',
        className
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport className='tw-p-1 tw-h-[var(--radix-select-trigger-height)] tw-max-h-[var(--radix-select-content-available-height)] tw-w-full tw-min-w-[var(--radix-select-trigger-width)]'>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'tw-py-1.5 tw-pl-8 tw-pr-2 tw-text-sm tw-font-semibold',
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    selectedIconColor?: IconColor | string
    rightIcon?: React.ReactNode
    truncate?: boolean
  }
>(
  (
    {
      className,
      selectedIconColor,
      truncate = true,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        'tw-relative tw-flex w-full tw-cursor-default tw-select-none tw-items-center tw-rounded-sm tw-py-1.5 tw-pl-8 tw-pr-2 tw-text-sm tw-outline-none focus:tw-bg-accent focus:tw-text-accent-foreground data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50',
        rightIcon && 'tw-pr-8',
        className
      )}
      {...props}
    >
      <span className='tw-absolute tw-left-2 tw-flex tw-h-3.5 tw-w-3.5 tw-items-center tw-justify-center'>
        <SelectPrimitive.ItemIndicator>
          <Icon
            icon={faCheck}
            className='tw-h-4 tw-w-4'
            color={selectedIconColor ?? IconColor.tertiary}
          />
        </SelectPrimitive.ItemIndicator>
      </span>

      <div className={cn(truncate && 'tw-truncate')}>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </div>

      {rightIcon && (
        <span className='tw-absolute tw-right-2.5 tw-flex tw-h-3.5 tw-w-3.5 tw-items-center tw-justify-center'>
          {rightIcon}
        </span>
      )}
    </SelectPrimitive.Item>
  )
)
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-tw-mx-1 tw-my-1 tw-h-px tw-bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
